import { Link } from "react-router";
import { Spreekballon } from "../Spreekballon";

export type ReportTopBarProps = {
  /** Close target — from resolveBackLink. Disabled renders an inert X. */
  back: { href: string; disabled?: boolean };
  label: string;
  closeAriaLabel: string;
};

/**
 * Slim top bar for the report result surface. Brand mark + wordmark, a
 * "Resultaat" pill, and a close-X routed to the from-aware back target.
 * Mirrors ExamTopBar's geometry so the two focus surfaces feel related.
 */
export function ReportTopBar({ back, label, closeAriaLabel }: ReportTopBarProps) {
  const xIcon = (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M6 6l12 12M18 6L6 18" />
    </svg>
  );
  const xClass =
    "inline-flex h-9 w-9 items-center justify-center rounded-full border border-[var(--border-strong)] bg-[var(--panel)] text-[var(--ink-muted)] transition-colors";

  return (
    <header className="sticky top-0 z-20 flex items-center gap-3 border-b border-[var(--border)] bg-[var(--panel)] px-4 py-3 sm:px-6 sm:py-4">
      <span className="flex items-center gap-2 shrink-0">
        <Spreekballon size={26} />
        <span className="hidden sm:inline font-display text-[17px] font-semibold tracking-tight">
          <span className="text-[var(--accent)]">A2</span> Easy
        </span>
      </span>

      <span className="inline-flex items-center rounded-full bg-[var(--accent-soft)] px-2.5 py-1 font-mono-badge text-[10px] uppercase tracking-wider text-[var(--accent)] shrink-0">
        {label}
      </span>

      <div className="flex-1" />

      {back.disabled ? (
        <span aria-hidden className={`${xClass} cursor-not-allowed opacity-50`}>
          {xIcon}
        </span>
      ) : (
        <Link
          to={back.href}
          aria-label={closeAriaLabel}
          className={`${xClass} hover:bg-[var(--panel-soft)] hover:text-[var(--ink)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--accent)] focus-visible:outline-offset-2`}
        >
          {xIcon}
        </Link>
      )}
    </header>
  );
}
